<!-- eslint-disable indent -->
<script lang="ts" setup>
import { ref, watch, onMounted, nextTick, onBeforeUnmount, computed } from "vue";
import debounce from 'lodash/debounce';
import AppLink from './AppLink.vue';

interface Props {
  time: string;
}

const props = defineProps<Props>();
const minutes = ref("00");
const amPm = ref("AM");
const hour = ref("01");

// Refs for the wheel elements
const hourWheel = ref<HTMLElement | null>(null);
const minuteWheel = ref<HTMLElement | null>(null);
const amPmWheel = ref<HTMLElement | null>(null);

// Add these new refs
const scrollTimeouts = ref({
  hour: null as any,
  minute: null as any,
  ampm: null as any
});

// Add a flag to track if we're currently snapping
const isSnapping = ref(false);

const getHourOptions = () => {
  return Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
};

const getMinuteOptions = () => {
  return Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
};

// Dynamic arrays for infinite scroll
const displayedHours = ref<string[]>([]);
const displayedMinutes = ref<string[]>([]);
const displayedAmPm = ref<string[]>([]);

const snapToCenter = (wheel: HTMLElement, type: 'hour' | 'minute' | 'ampm') => {
  if (isSnapping.value) return;

  isSnapping.value = true;
  const itemHeight = 30;
  const viewportHeight = wheel.clientHeight;
  const paddingOffset = 60; // This matches the padding in CSS

  let currentValue;
  let options;

  if (type === 'hour') {
    currentValue = hour.value;
    options = getHourOptions();
  } else if (type === 'minute') {
    currentValue = minutes.value;
    options = getMinuteOptions();
  } else {
    currentValue = amPm.value;
    options = ['AM', 'PM'];
  }

  const index = options.indexOf(currentValue);
  const targetScroll = (index * itemHeight) - Math.floor((viewportHeight - itemHeight) / 2) + paddingOffset;

  wheel.scrollTo({
    top: targetScroll,
    behavior: 'smooth'
  });

  // Reset the snapping flag after animation completes
  setTimeout(() => {
    isSnapping.value = false;
  }, 300);
};

const debouncedSnap = debounce((wheel: HTMLElement, type: 'hour' | 'minute' | 'ampm') => {
  snapToCenter(wheel, type);
}, 500);

const handleScroll = (event: Event, type: 'hour' | 'minute' | 'ampm') => {
  if (isSnapping.value || initializing.value) return;

  const wheel = event.target as HTMLElement;
  const itemHeight = 30;
  const viewportHeight = wheel.clientHeight;
  // Adjust middle offset by subtracting half of item height for better centering
  const middleOffset = Math.floor(viewportHeight / 2) - (itemHeight / 2);
  const scrollTop = wheel.scrollTop;

  // Clear existing timeout for this wheel
  if (scrollTimeouts.value[type]) {
    clearTimeout(scrollTimeouts.value[type]);
  }

  // Calculate which item should be selected based on scroll position
  if (type === 'hour') {
    const hours = getHourOptions();
    // Adjusted the offset calculation
    const index = Math.round((scrollTop + middleOffset - 60) / itemHeight) % hours.length;
    const selectedHour = hours[index];
    if (hour.value !== selectedHour) {
      hour.value = selectedHour;
    }
  } else if (type === 'minute') {
    const minutesList = getMinuteOptions();
    const index = Math.round((scrollTop + middleOffset - 60) / itemHeight) % minutesList.length;
    const selectedMinute = minutesList[index];
    if (minutes.value !== selectedMinute) {
      minutes.value = selectedMinute;
    }
  } else {
    const amPmValues = ['AM', 'PM'];
    const index = Math.round((scrollTop + middleOffset - 60) / itemHeight) % amPmValues.length;
    const selectedAmPm = amPmValues[index];
    if (amPm.value !== selectedAmPm) {
      amPm.value = selectedAmPm;
    }
  }

  // Set new timeout for snapping
  debouncedSnap(wheel, type);
};

const initializing = ref(true);
onMounted(() => {
  const hours = getHourOptions();
  const minutesList = getMinuteOptions();
  const amPmValues = ['AM', 'PM'];

  // Initialize with repeated values for infinite scroll
  displayedHours.value = [...hours];
  displayedMinutes.value = [...minutesList];
  displayedAmPm.value = [...amPmValues];

  // Increase timeout to ensure DOM is ready
  setTimeout(() => {
    console.log('Scrolling to initial positions:', {
      hour: hour.value,
      minutes: minutes.value,
      amPm: amPm.value
    });

    // Set initial scroll positions
    if (hourWheel.value) {
      const hourIndex = hours.indexOf(hour.value);
      const scrollTop = (hourIndex * 30);
      console.log('Hour wheel position:', {
        hourIndex,
        scrollTop,
        hours,
        currentHour: hour.value
      });
      hourWheel.value.scrollTop = scrollTop;
    }

    if (minuteWheel.value) {
      const minuteIndex = minutesList.indexOf(minutes.value);
      const scrollTop = (minuteIndex * 30);
      minuteWheel.value.scrollTop = scrollTop;
    }

    if (amPmWheel.value) {
      const amPmIndex = amPmValues.indexOf(amPm.value);
      const scrollTop = (amPmIndex * 30);
      amPmWheel.value.scrollTop = scrollTop;
    }

    // Set initializing to false after all scrolling is done
    initializing.value = false;
  }, 100);
});

const setupTime = (time: string) => {
  const [currentHour, currentMinute] = time.split(":");
  minutes.value = currentMinute;
  hour.value =
    currentHour === "00"
      ? "12"
      : +currentHour > 12
        ? (+currentHour - 12).toString().padStart(2, "0")
        : currentHour;
  amPm.value = +currentHour >= 12 ? "PM" : "AM";
};

if (props.time) {
  setupTime(props.time);
}

const emit = defineEmits(["done"]);

watch(
  () => props.time,
  (newValue) => {
    setupTime(newValue);
  }
);

// const timeChanged = () => {
//   const tmpHour = hour.value === "12" ? "00" : hour.value;
//   const time =
//     amPm.value === "AM"
//       ? `${tmpHour.padStart(2, "0")}:${minutes.value}`
//       : `${(+tmpHour + 12).toString().padStart(2, "0")}:${minutes.value}`;
//   if (props.time !== time && !initializing.value) {
//     emit("timeChanged", time);
//   }
// };

// Add watch for debugging
watch(() => props.time, (newTime, oldTime) => {
  if (newTime === oldTime) return;
  console.log('TimePicker time prop changed:', {
    newTime,
    hour: hour.value,
    minutes: minutes.value,
    amPm: amPm.value
  });
}, { immediate: true });

// Add computed property for formatted display time
const formatDisplayTime = computed(() => {
  const formattedHour = hour.value.padStart(2, '0');
  return `${formattedHour}:${minutes.value} ${amPm.value.toLocaleLowerCase()}`;
});

// Add handler for Done click
const handleDone = () => {
  const tmpHour = hour.value === "12" ? "00" : hour.value;
  const time =
    amPm.value === "AM"
      ? `${tmpHour.padStart(2, "0")}:${minutes.value}`
      : `${(+tmpHour + 12).toString().padStart(2, "0")}:${minutes.value}`;
  emit("done", time);
};

// Clean up debounced function when component is unmounted
onBeforeUnmount(() => {
  debouncedSnap.cancel();
});
</script>
<template>
  <div class="time-picker">
    <!-- Add header -->
    <div class="flex items-center justify-between p-2">
      <div class="text-lg font-medium">
        {{ formatDisplayTime }}
      </div>
      <AppLink @click="handleDone">Done</AppLink>
    </div>
    
    <div class="picker-container">
      <!-- Hours -->
      <div class="picker-column">
        <div class="picker-viewport">
          <div ref="hourWheel" class="picker-wheel" @scroll="handleScroll($event, 'hour')">
            <div v-for="h in displayedHours" :key="h" :class="['picker-item', { 'selected': hour === h }]">
              {{ h }}
            </div>
          </div>
        </div>
      </div>

      <!-- Minutes -->
      <div class="picker-column">
        <div class="picker-viewport">
          <div ref="minuteWheel" class="picker-wheel" @scroll="handleScroll($event, 'minute')">
            <div v-for="m in displayedMinutes" :key="m" :class="['picker-item', { 'selected': minutes === m }]">
              {{ m }}
            </div>
          </div>
        </div>
      </div>

      <!-- AM/PM -->
      <div class="picker-column">
        <div class="picker-viewport">
          <div ref="amPmWheel" class="picker-wheel" @scroll="handleScroll($event, 'ampm')">
            <div v-for="p in displayedAmPm" :key="p" :class="['picker-item', { 'selected': amPm === p }]">
              {{ p }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.time-picker {
  width: 200px;
  margin: 0 auto;
  background: white;
  border-radius: 4px;
  overflow: hidden;
}

.picker-container {
  display: flex;
  justify-content: space-between;
  border: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
  overflow: hidden;
}

.picker-column {
  flex: 1;
  position: relative;
  height: 150px;
  overflow: hidden;
  border-right: 1px solid #eee;
}

.picker-column:last-child {
  border-right: none;
}

.picker-viewport {
  position: relative;
  height: 100%;
}

.picker-wheel {
  position: absolute;
  width: 100%;
  text-align: center;
  height: 100%;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
  padding: 60px 0;
}

.picker-wheel::-webkit-scrollbar {
  display: none;
}

.picker-item {
  height: 30px;
  line-height: 30px;
  user-select: none;
  color: #666;
  transition: color 0.2s ease;
}

.picker-item.selected {
  color: var(--color-action);
  font-weight: bold;
  transition: color 0.2s ease;
}

/* Selection indicator */
.picker-viewport::before,
.picker-viewport::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 60px;
  pointer-events: none;
  z-index: 1;
}

.picker-viewport::before {
  top: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0));
}

.picker-viewport::after {
  bottom: 0;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0));
}

/* Selection highlight */
.picker-viewport::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 30px;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.05);
  pointer-events: none;
  z-index: 1;
}
</style>
